import React from "react";
import { api, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileSpreadsheet, Download } from "lucide-react";
import { useI18n } from "@/i18n";

export default function Reports() {
  const { language, t } = useI18n();
  const { data: catalog } = useQuery({
    queryKey: ["reports-catalog", language],
    queryFn: async () => unwrap(await api.get("/reports/catalog", { params: { lang: language } })),
  });
  const download = async (code, title) => {
    try {
      const resp = await api.get(`/reports/${code}.xlsx`, { params: { lang: language }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${code}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success(`${title} ${t("finance.downloaded")}`);
    } catch (e) { toast.error(e?.response?.data?.detail?.error?.message || t("finance.downloadFailed")); }
  };
  return (
    <div className="p-6 lg:p-8" data-testid="reports-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("finance.reports")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("finance.reportsDescription")}</p>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {(catalog || []).map((r) => (
          <Card key={r.code} className="border-[var(--subtle)]" data-testid={`report-${r.code}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge className="bg-[var(--accent-bg)] text-[var(--accent)] border-0">XLSX</Badge>
                <FileSpreadsheet className="h-5 w-5 text-[var(--text-muted)]" />
              </div>
              <CardTitle className="text-base font-display mt-2">{r.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[var(--text-secondary)] mb-4">{r.description}</p>
              <Button size="sm" onClick={() => download(r.code, r.title)} data-testid={`download-${r.code}`} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white w-full">
                <Download className="h-4 w-4 mr-1.5" /> {t("finance.download")}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
